import React, { useState } from 'react';
import { FieldError } from 'react-hook-form';
import DynamicSelect from '../../../../Components/Base/DynamicSelect/DynamicSelect';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import WorkplaceOrWorkplaceMemberSelectItem from './WorkplaceOrWorkplaceMemberSelectItem';
import {
	useLazyRootWorkplaceDoctorsQuery,
	useLazySimpleWorkplacesQuery,
} from '../../../../graphql/graphql';

interface WorkplaceOrWorkplaceMemberSelectProps {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	error?: FieldError;
	placeholder?: string;
	filter?: {
		workplaceId?: string;
		searchWorkplaceMembersOnly?: boolean;
		searchWorkplaceOnly?: boolean;
		searchWorkplaceNormalOnly?: boolean;
	};
	includeNurses?: boolean;
}

export interface WorkplaceOrWorkplaceMemberSelectValue extends SelectValue {
	item: any;
}

const WorkplaceOrWorkplaceMemberSelect: React.FC<
	WorkplaceOrWorkplaceMemberSelectProps
> = props => {
	const { value, onChange, filter, includeNurses = true } = props;
	const [defaultValue, setDefaultValue] = useState<SelectValue | undefined>(
		value,
	);
	const [searchDoctors] = useLazyRootWorkplaceDoctorsQuery();
	const [searchWorkplaces] = useLazySimpleWorkplacesQuery();

	const handleChange = (val: any) => {
		setDefaultValue(val);
		onChange?.(val);
	};

	const handleSearchItem = async (
		searchInput: string,
	): Promise<WorkplaceOrWorkplaceMemberSelectValue[]> => {
		const { rootWorkplaceDoctors } = await searchDoctors({
			fulltext: searchInput,
			includeNurses: includeNurses,
		}).unwrap();

		const { workplaces } = await searchWorkplaces({
			fulltext: searchInput,
		}).unwrap();

		const items: WorkplaceOrWorkplaceMemberSelectValue[] = [];

		if (!filter?.searchWorkplaceOnly) {
			for (const rootWorkplaceDoctor of rootWorkplaceDoctors?.data || []) {
				if (rootWorkplaceDoctor) {
					if (!filter?.workplaceId) {
						items.push({
							value: rootWorkplaceDoctor.id,
							label: '',
							item: rootWorkplaceDoctor,
						});
					} else {
						if (filter.workplaceId === rootWorkplaceDoctor?.workplace?.id) {
							items.push({
								value: rootWorkplaceDoctor.id,
								label: '',
								item: rootWorkplaceDoctor,
							});
						}
					}
				}
			}
		}

		if (!filter?.searchWorkplaceMembersOnly) {
			for (const workplace of workplaces?.data || []) {
				if (workplace) {
					if (
						filter?.searchWorkplaceNormalOnly &&
						workplace.type !== 'normal'
					) {
						continue;
					}
					items.push({
						value: workplace.id,
						label: '',
						item: workplace,
					});
				}
			}
		}

		return items;
	};

	return (
		<DynamicSelect
			searchItems={handleSearchItem}
			{...props}
			onChange={handleChange}
			value={defaultValue}
			ItemTemplate={WorkplaceOrWorkplaceMemberSelectItem}
			portal
		/>
	);
};

export default WorkplaceOrWorkplaceMemberSelect;
